/// <amd-module name="Feed/Legacy/DataServices/WebPageDataService" />
import { Services, VssService } from "VSS/Platform/Context";
import { IdentityDescriptor } from "VSS/Platform/Contracts/Identities";
import { IVssContributionService } from "VSS/Platform/Contribution";

import { DataProviderConstants } from "Feed/Common/Constants/Constants";
import { UpstreamSource } from "Feed/Common/Contracts/Feed";
import { FeedBuildIdentity, FeedsResult, PackageDetailsResult, PackagesResult, WebPageData } from "Feed/Common/Contracts/WebPage";
import { IFeedWebPageDataService } from "Feed/Common/Services/IFeedWebPageDataService";

export class HubWebPageDataService extends VssService implements IFeedWebPageDataService {
    public getInitialFeedsResult(): FeedsResult {
        return this.webPageData.feedsResult;
    }

    public getInitialPackagesResult(): PackagesResult {
        return this.webPageData.packagesResult;
    }

    public getInitialPackageDetailsResult(): PackageDetailsResult {
        return this.webPageData.packageDetailsResult;
    }

    public getPageSize(): number {
        return this.webPageData.pageSize;
    }

    public getBatchOperationPageSize(): number {
        return this.webPageData.batchOperationPageSize;
    }

    public isIvyUIEnabled(): boolean {
        return this.isFeatureEnabled("Packaging.Feed.WebAccess.IvyUI");
    }

    public getNpmUpstreamNameConflictFeatureFlag(): boolean {
        return this.isFeatureEnabled("Packaging.Feed.Npm.AllowUpstreamNameConflict");
    }

    public userCanAdministerFeeds(): boolean {
        return this.webPageData.userCanAdministerFeeds;
    }

    public userCanCreateFeed(): boolean {
        return this.webPageData.userCanCreateFeed;
    }

    public isOrgAadBacked(): boolean {
        return this.webPageData.isOrgAadBacked;
    }

    public isGlobalPermMappedIdentifiersEnabled(): boolean {
        return this.isFeatureEnabled("Packaging.Feed.GlobalPermissionsMapWellKnownIdentifier");
    }

    public isProjectScopedUPackEnabled(): boolean {
        return this.isFeatureEnabled("Packaging.Feed.ProjectScopedUPackUI");
    }

    public getDefaultPublicUpstreamSources(): UpstreamSource[] {
        return this.webPageData.defaultPublicUpstreamSources;
    }

    public getCollectionBuildIdentity(): FeedBuildIdentity {
        return this.webPageData.collectionBuildIdentity;
    }

    public getProjectBuildIdentity(): FeedBuildIdentity {
        return this.webPageData.projectBuildIdentity;
    }

    public getEveryoneGroup(): IdentityDescriptor {
        return this.webPageData.everyoneGroup;
    }

    public getEveryoneGroupId(): string {
        return this.webPageData.everyoneGroupId;
    }

    public getCurrentUserDescriptor(): string {
        return this.webPageData.currentUserDescriptor;
    }

    public publicAccessMapping(): string {
        return this.webPageData.publicAccessMapping;
    }

    public getProjectCollectionAdminGroupId(): string {
        return this.webPageData.projectCollectionAdminGroupId;
    }

    public retentionPolicyLimits(): {
        minimumCountLimit: number;
        maximumCountLimit: number;
        minimumDaysToKeepLimit: number;
        maximumDaysToKeepLimit: number;
        retentionPolicyCountLimitDefault: number;
        retentionPolicyDaysToKeepLimitDefault: number;
    } {
        const data = this.webPageData;
        return {
            minimumCountLimit: data.retentionPolicyMinimumCountLimit,
            maximumCountLimit: data.retentionPolicyMaximumCountLimit,
            minimumDaysToKeepLimit: data.retentionPolicyMinimumDaysToKeepLimit,
            maximumDaysToKeepLimit: data.retentionPolicyMaximumDaysToKeepLimit,
            retentionPolicyCountLimitDefault: data.retentionPolicyCountLimitDefault,
            retentionPolicyDaysToKeepLimitDefault: data.retentionPolicyDaysToKeepLimitDefault
        };
    }

    public minimumSnapshotInstanceCount(): number {
        return this.webPageData.minimumSnapshotInstanceCount;
    }

    public isCustomPublicUpstreamsFeatureEnabled(): boolean {
        return this.isFeatureEnabled("Packaging.Feed.CustomPublicUpstreams");
    }

    public isProjectScopedUpstreamsFeatureEnabled(): boolean {
        return this.isFeatureEnabled("Packaging.Feed.ProjectScopedUpstreams");
    }

    public isPackageMetricsEnabled(): boolean {
        return this.isFeatureEnabled("Packaging.Feed.WebAccess.Metrics");
    }

    public isSmartDependenciesEnabled(): boolean {
        return this.isFeatureEnabled("Packaging.Feed.SmartDependencies");
    }

    public isProvenanceEnabled(): boolean {
        return this.isFeatureEnabled("Packaging.Feed.WebAccess.Provenance");
    }

    public isProjectScopedUpstreamsEnabled(): boolean {
        return this.isFeatureEnabled("Packaging.Feed.ProjectScopedUpstreams");
    }

    public isFeedPackagesDataProviderEnabled(): boolean {
        return this.isFeatureEnabled("Packaging.Feed.FeedPackagesDataProvider");
    }

    public isServiceEndpointUpstreamsFeatureEnabled(): boolean {
        return this.isFeatureEnabled("Packaging.Feed.Upstreams.SupportServiceEndpoint");
    }

    public getUpstreamSourceLimit(): number {
        return this.webPageData.upstreamSourceLimit;
    }

    public isWebPageDataAvailable(): boolean {
        return Object.keys(this.webPageData).length > 0;
    }

    private get webPageData(): WebPageData {
        if (!this._data) {
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            this._data = contributionService.getData<WebPageData>(DataProviderConstants.PackageHubDataProvider);
        }
        return this._data || ({} as WebPageData);
    }

    private isFeatureEnabled(featureFlag: string): boolean {
        const data = this.webPageData;
        return data && data.enabledFeatures && data.enabledFeatures.indexOf(featureFlag) !== -1;
    }

    private _data: WebPageData;
}

Services.add("IFeedWebPageDataService", { serviceFactory: HubWebPageDataService });
